import React, { useState, useEffect, useRef } from "react";
import {
  DeleteOutlined,
  AudioOutlined,
  CheckOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Button, Typography, Space, message, Alert } from "antd";
import mikeIcon from "../../../../assets/fileicons/mikeIcon.svg";
import "./VoiceRecorder.css";

const { Text } = Typography;

export default function AiVoiceRecording({
  setRecordedFile,
  startRecord,
  setStartRecord,
  onRecordSubmit,
  stopAllRecordings,
  type,
  addForm,
}) {
  const [recording, setRecording] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const permissionRequestPendingRef = useRef(false);
  const micPermissionDeniedRef = useRef(false);
  const permissionToastShownRef = useRef(false);

  // Start recording function
  const startRecording = async () => {
    if (permissionRequestPendingRef.current || isRecording) return;

    if (micPermissionDeniedRef.current) {
      setStartRecord(false);
      return;
    }

    permissionRequestPendingRef.current = true;

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      micPermissionDeniedRef.current = false;
      permissionToastShownRef.current = false;

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      // Collect audio data
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);

        const timestamp = new Date().toLocaleString();
        const newRecording = {
          id: Date.now(),
          url,
          blob,
          timestamp,
          uploaded: false,
        };

        setRecordedFile(blob);
        setRecording(newRecording);
        setIsRecording(false);

        // Clean up stream
        cleanupStream();
      };

      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting recording:", error);
      micPermissionDeniedRef.current = true;
      if (!permissionToastShownRef.current) {
        message.error("Failed to access microphone. Please check permissions.");
        permissionToastShownRef.current = true;
      }
      setStartRecord(false);
    } finally {
      permissionRequestPendingRef.current = false;
    }
  };

  // Stop recording function
  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  };

  // Clean up stream
  const cleanupStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }
  };

  // Force stop all recordings
  const forceStopRecording = () => {
    stopRecording();
    cleanupStream();
    permissionRequestPendingRef.current = false;
    setIsRecording(false);
    setStartRecord(false);
  };

  // Delete recording
  const deleteRecording = () => {
    if (recording?.url) {
      URL.revokeObjectURL(recording.url);
    }
    setRecording(null);
    setRecordedFile(null);
    setUploadStatus("");
    message.info("Recording deleted");
  };

  // Watch startRecord prop changes
  useEffect(() => {
    if (startRecord && !isRecording) {
      startRecording();
    } else if (!startRecord && isRecording) {
      stopRecording();
    }
  }, [startRecord]);

  // Expose force stop function
  useEffect(() => {
    if (stopAllRecordings) {
      stopAllRecordings.current = forceStopRecording;
    }
  }, [stopAllRecordings]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      forceStopRecording();
      if (recording?.url) {
        URL.revokeObjectURL(recording.url);
      }
    };
  }, []);

  // Handle manual click
  const handleMicClick = () => {
    if (isUploading) return;

    if (isRecording) {
      setStartRecord(false);
      stopRecording();
    } else {
      micPermissionDeniedRef.current = false;
      permissionToastShownRef.current = false;
      setStartRecord(true);
    }
  };

  return (
    <>
      <Space
        direction="vertical"
        align="center"
        style={{ width: "100%", marginBottom: 24 }}
      >
        <div className="recorder-container">
          {!addForm ? (
            // 🎧 EDIT MODE — compact inline layout with animation
            <div className="recorder-inline">
              <div
                className={`mic-wave-wrapper ${isRecording ? "recording" : ""}`}
                onClick={handleMicClick}
              >
                <AudioOutlined
                  style={{
                    fontSize: 22,
                    color: "#1677ff",
                    cursor: "pointer",
                  }}
                  className="mic-icon"
                />
              </div>

              <audio
                controls
                src={recording?.url}
                className="custom-audio-inline"
              />

              <Button
                icon={<CheckOutlined />}
                size="small"
                type="primary"
                onClick={onRecordSubmit}
                disabled={!recording?.url}
              />
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                onClick={deleteRecording}
                disabled={!recording?.url}
              />
            </div>
          ) : (
            // 🎙️ ADD MODE — your existing large mic setup
            <div
              className={isRecording ? "recording-pulse" : ""}
              style={{
                cursor: isUploading ? "not-allowed" : "pointer",
                position: "relative",
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={handleMicClick}
            >
              {isUploading ? (
                <LoadingOutlined style={{ fontSize: 40 }} />
              ) : (
                <img
                  src={mikeIcon}
                  height={120}
                  style={{
                    transition: "opacity 0.2s ease-in-out",
                  }}
                  alt="Microphone"
                />
              )}
            </div>
          )}
        </div>

        <Text strong>
          {isUploading
            ? "Uploading recording..."
            : isRecording
            ? "Recording... (Click to stop)"
            : recording?.url
            ? "Click to start recording to replace the previous recording"
            : "Click to start recording"}
        </Text>

        {uploadStatus && (
          <Text
            type={
              uploadStatus.includes("successful")
                ? "success"
                : uploadStatus.includes("failed")
                ? "danger"
                : "secondary"
            }
          >
            {uploadStatus}
          </Text>
        )}
      </Space>

      {/* Recording Display */}
      {recording?.url && addForm && (
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <audio
            controls
            src={recording.url}
            style={{
              flex: 1,
            }}
          />

          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={deleteRecording}
          />
        </div>
      )}
      <br />
      <Alert
        message="Note"
        description={
          type === "grbs" ? (
            <Typography.Text>
              Please mention all values clearly for example, say{" "}
              <strong>
                "Time: 10 AM, GRBS: 400, Insulin: 100," and so on.
              </strong>
            </Typography.Text>
          ) : type === "vitals" ? (
            <Typography.Text>
              Please mention all vitals clearly for example, say{" "}
              <strong>
                "Time: 10 AM, Temperature: 98.6°F, Pulse Rate: 75, SPO2: 97%,"
                and so on.
              </strong>
            </Typography.Text>
          ) : (
            <Typography.Text>
              Please mention all values clearly for example, say{" "}
              <strong>
                "Time: 10 AM, Drain: 10 ml, Flush: 20ml, Gi Losses: 40ml," and
                so on.
              </strong>
            </Typography.Text>
          )
        }
        type="warning"
        showIcon
        icon={<AudioOutlined style={{ color: "red" }} />}
      />
    </>
  );
}
